/**
 * Reddit Pixel — browser side.
 *
 * The base pixel is injected in the root layout and fires `PageVisit` on load.
 * This module is for everything after that: the conversion events Reddit Ads
 * optimises campaigns against.
 *
 * Every conversion also goes to Reddit's Conversions API server-side (see
 * `lib/server/reddit-conversions.ts`). The two are deliberately duplicated —
 * ad blockers and Safari's ITP drop a meaningful share of browser events, and
 * the server copy is not blockable. Reddit collapses the pair back into one
 * conversion whenever both carry the same `conversion_id`, so passing a stable
 * id is what keeps the duplication from double-counting.
 */

/**
 * The pixel associated with the SupplyAutomate Reddit ad account.
 *
 * Keep this aligned with Reddit Ads > Events Manager. Campaigns and ad groups
 * can only optimise against pixels associated with their own ad account; using
 * another account's pixel records activity where this account cannot use it.
 *
 * Overridable so a preview deployment can point at a test pixel without a code
 * change; the default keeps a missing env var from silently switching tracking
 * off.
 */
export const REDDIT_PIXEL_ID =
  process.env.NEXT_PUBLIC_REDDIT_PIXEL_ID ?? 'a2_eki2c3zb9aqp';

/**
 * Reddit's standard event names. Anything outside this list has to be sent as
 * `Custom` with a `customEventName`, and standard events are what the campaign
 * objectives in Reddit Ads can actually optimise for — so prefer these.
 */
/**
 * Handoff for the signup conversion.
 *
 * Whether a signup is real is only knowable on the server — it is the auth
 * callback that can tell a confirmed first authentication from an ordinary
 * login. But the pixel has to fire in the browser, several redirects later. So
 * the server drops the conversion id in this cookie and the browser picks it
 * up on whatever page it lands on, fires the event, and clears it.
 *
 * Readable by client JS by necessity, short-lived, and opaque: it carries a
 * hash, never the user id.
 */
export const REDDIT_SIGNUP_COOKIE = 'sa_rdt_signup';

/** Long enough to survive the redirect chain, short enough not to re-fire later. */
export const REDDIT_SIGNUP_COOKIE_MAX_AGE = 60 * 5;

export type RedditStandardEvent =
  | 'PageVisit'
  | 'ViewContent'
  | 'Search'
  | 'AddToCart'
  | 'AddToWishlist'
  | 'Lead'
  | 'SignUp'
  | 'Purchase';

export interface RedditEventPayload {
  /**
   * Shared with the server-side copy of this event so Reddit deduplicates the
   * two. Omitting it means the conversion is counted twice.
   */
  conversionId?: string;
  value?: number;
  currency?: string;
  itemCount?: number;
  customEventName?: string;
}

type RedditPixel = (
  command: 'init' | 'track',
  eventName: string,
  payload?: Record<string, unknown>,
) => void;

declare global {
  interface Window {
    rdt?: RedditPixel;
  }
}

/**
 * How long to wait for the pixel to appear before giving up on an event.
 *
 * The base snippet is loaded with next/script's `afterInteractive`, so
 * `window.rdt` does not exist during the first render — and a conversion fired
 * from a `useEffect` can easily beat it. Checking once and bailing would drop
 * exactly the events that matter most. Waiting is safe: the snippet installs a
 * `callQueue`, so the moment `rdt` exists the call is buffered even if
 * pixel.js itself is still downloading.
 */
const PIXEL_READY_TIMEOUT_MS = 10_000;
const PIXEL_READY_POLL_MS = 100;

function whenPixelReady(callback: () => void) {
  if (typeof window === 'undefined') {
    return;
  }

  if (typeof window.rdt === 'function') {
    callback();

    return;
  }

  const startedAt = Date.now();

  const timer = window.setInterval(() => {
    if (typeof window.rdt === 'function') {
      window.clearInterval(timer);
      callback();

      return;
    }

    // Blocked by an extension, or the CDN is down. The server-side Conversions
    // API copy of this event is the fallback, so stop quietly.
    if (Date.now() - startedAt > PIXEL_READY_TIMEOUT_MS) {
      window.clearInterval(timer);
    }
  }, PIXEL_READY_POLL_MS);
}

/**
 * Fire a Reddit conversion event.
 *
 * Safe to call unconditionally: on the server, or when the pixel is blocked,
 * this is a no-op rather than a thrown `rdt is not defined`. Tracking must
 * never break a signup.
 */
export function trackRedditEvent(
  eventName: RedditStandardEvent | 'Custom',
  payload: RedditEventPayload = {},
) {
  const { conversionId, value, currency, itemCount, customEventName } = payload;

  // Reddit ignores keys it does not recognise, but it does *not* ignore an
  // explicit undefined — it serialises it — so the payload is built up only
  // from what is actually present.
  const eventPayload: Record<string, unknown> = {};

  // `conversionId`, camelCase — the browser pixel and the Conversions API
  // disagree on spelling, and the API's `conversion_id` is silently dropped
  // here. Getting this wrong costs nothing visible: the event still arrives,
  // just with no dedup key, so every conversion is counted twice. Verified
  // against the outgoing rp.gif, which echoes it back as `m.conversionId`
  // (SHA-256'd by the pixel before it goes).
  if (conversionId) {
    eventPayload.conversionId = conversionId;
  }

  if (typeof value === 'number') {
    eventPayload.value = value;
  }

  if (currency) {
    eventPayload.currency = currency;
  }

  if (typeof itemCount === 'number') {
    eventPayload.itemCount = itemCount;
  }

  if (customEventName) {
    eventPayload.customEventName = customEventName;
  }

  whenPixelReady(() => {
    try {
      window.rdt?.('track', eventName, eventPayload);
    } catch (error) {
      // A pixel that throws must not take a checkout or a signup down with it.
      console.error('Reddit pixel event failed', error);
    }
  });
}
